import React, { useEffect, useRef } from "react";

const WA_NUMBER = "5491124289800";
const WA_MSG = encodeURIComponent(
  "Hola, ¿cómo estás? Me comunico desde la web de Magic Kingdom Eventos. ¿En qué me podés ayudar?"
);

const paymentMethods = [
  { icon: "💵", label: "Efectivo" },
  { icon: "🏦", label: "Transferencia" },
  { icon: "💳", label: "Tarjetas de crédito con alias" },
];

export default function Contact() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target
              .querySelectorAll(".reveal, .reveal-left, .reveal-right")
              .forEach((el) => el.classList.add("visible"));
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="contacto"
      ref={sectionRef}
      className="py-24 bg-background relative overflow-hidden"
      aria-labelledby="contacto-title"
    >
      {/* Decorations */}
      <div className="absolute top-0 left-0 w-80 h-80 bg-accent/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-primary/5 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <p className="section-subtitle mb-3">Hablemos</p>
          <h2 id="contacto-title" className="section-title mb-4">
            Contacto
          </h2>
          <p className="font-body text-text/70 text-lg max-w-xl mx-auto">
            Consultá disponibilidad y armamos juntos el festejo de tus sueños.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* WhatsApp block */}
          <div className="reveal-left">
            <div className="bg-primary rounded-3xl p-8 md:p-10 relative overflow-hidden">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-5 pointer-events-none">
                <svg width="100%" height="100%">
                  <defs>
                    <pattern id="dots2" width="24" height="24" patternUnits="userSpaceOnUse">
                      <circle cx="12" cy="12" r="1.5" fill="white" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#dots2)" />
                </svg>
              </div>

              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-[#25D366] flex items-center justify-center mb-6 whatsapp-pulse">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>

                <p className="font-display text-white text-2xl font-semibold mb-1">
                  Hola, ¿cómo estás?
                </p>
                <p className="font-body text-white/70 mb-2">
                  Gracias por comunicarte con{" "}
                  <strong className="text-gold-light">Magic Kingdom Eventos</strong>.
                </p>
                <p className="font-body text-white/70 mb-8">
                  ¿En qué te puedo ayudar?
                </p>

                <a
                  href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BA5A] text-white font-body font-bold text-lg py-4 px-8 rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
                  aria-label="Escribir por WhatsApp a Magic Kingdom Eventos"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Escribinos por WhatsApp
                </a>

                {/* Hours note */}
                <p className="font-body text-white/40 text-xs text-center mt-4 italic">
                  Los horarios de atención varían según los eventos de la semana.
                </p>
              </div>
            </div>
          </div>

          {/* Info block */}
          <div className="reveal-right space-y-6">
            {/* Contact info */}
            <div className="bg-surface rounded-3xl p-6 border border-accent/20 shadow-card">
              <h3 className="font-display text-primary text-xl font-bold mb-5">
                Información de contacto
              </h3>
              <ul className="space-y-4" role="list">
                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-body text-text/50 text-xs uppercase tracking-wide mb-0.5">Teléfono</p>
                    <a
                      href="tel:+5491124289800"
                      className="font-body text-text font-semibold hover:text-primary transition-colors"
                      aria-label="Llamar a Magic Kingdom Eventos"
                    >
                      11 2428-9800
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-body text-text/50 text-xs uppercase tracking-wide mb-0.5">Dirección</p>
                    <address className="font-body text-text font-semibold not-italic">
                      Felipe Amoedo 1660,{" "}
                      <span className="font-normal text-text/70">Quilmes, Buenos Aires</span>
                    </address>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-body text-text/50 text-xs uppercase tracking-wide mb-0.5">Horarios</p>
                    <p className="font-body text-text/70 text-sm">
                      Los horarios de atención varían semana a semana según los eventos programados.
                      Escribinos por WhatsApp para consultar disponibilidad.
                    </p>
                  </div>
                </li>
              </ul>

              {/* Social */}
              <div className="flex gap-3 mt-5 pt-5 border-t border-accent/20">
                <a
                  href="https://www.instagram.com/magic_kingdom_eventos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 flex-1 justify-center bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white font-body text-sm font-semibold py-2.5 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-md"
                  aria-label="Seguir en Instagram"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                  Instagram
                </a>
                <a
                  href="https://www.facebook.com/share/1DfqZw8kzD/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 flex-1 justify-center bg-[#1877F2] text-white font-body text-sm font-semibold py-2.5 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-md hover:bg-[#1565C0]"
                  aria-label="Seguir en Facebook"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Facebook
                </a>
              </div>
            </div>

            {/* Payment methods */}
            <div className="bg-surface rounded-3xl p-6 border border-accent/20 shadow-card">
              <h3 className="font-display text-primary text-xl font-bold mb-4">
                Medios de pago
              </h3>
              <ul className="grid grid-cols-3 gap-3" role="list">
                {paymentMethods.map((p, i) => (
                  <li
                    key={i}
                    className="flex flex-col items-center gap-2 bg-background rounded-2xl p-3 text-center border border-accent/10"
                  >
                    <span className="text-2xl" role="img" aria-label={p.label}>
                      {p.icon}
                    </span>
                    <span className="font-body text-text text-xs font-medium leading-tight">
                      {p.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
